import { useEffect, useRef, useState } from 'react';
import { useFocusTrap } from '../../lib/useFocusTrap';

const STORAGE_KEY = 'ninpo-cookie-consent.v1';

type Decision = 'accepted' | 'rejected';

const readDecision = (): Decision | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'accepted' || value === 'rejected' ? (value as Decision) : null;
  } catch {
    return null;
  }
};

const writeDecision = (decision: Decision) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, decision);
  } catch {
    /* swallow */
  }
};

export default function CookieConsentBanner() {
  const [decision, setDecision] = useState<Decision | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const open = hydrated && !decision;

  useEffect(() => {
    setDecision(readDecision());
    setHydrated(true);
  }, []);

  useFocusTrap(open, panelRef);

  if (!open) {
    return null;
  }

  const handleChoice = (choice: Decision) => {
    writeDecision(choice);
    setDecision(choice);
    try {
      window.dispatchEvent(new CustomEvent('cookie-consent', { detail: { choice } }));
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed bottom-4 left-4 right-4 z-[12500] mx-auto max-w-3xl rounded-2xl bg-black/90 text-white p-5 shadow-2xl border border-white/15 flex flex-col md:flex-row md:items-center md:justify-between gap-4 outline-none"
    >
      <div className="text-xs leading-relaxed text-white/85">
        We use essential cookies to keep you signed in and analytics cookies to improve the
        experience. Read our{' '}
        <a className="underline hover:text-ninpo-lime" href="/privacy">privacy policy</a> and{' '}
        <a className="underline hover:text-ninpo-lime" href="/terms">terms of service</a>.
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          type="button"
          onClick={() => handleChoice('rejected')}
          className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs uppercase tracking-[0.25em]"
        >
          Reject non-essential
        </button>
        <button
          type="button"
          onClick={() => handleChoice('accepted')}
          className="px-4 py-2 rounded-lg bg-ninpo-lime text-ninpo-black font-bold text-xs uppercase tracking-[0.25em]"
        >
          Accept all
        </button>
      </div>
    </div>
  );
}
